import { FC, useContext, useRef } from "react";
import { ReportCustomizationContext } from "../ReportCustomizationContext";
import { MembershipType } from "@app/exports/enumerations";
import ExportService from "@app/exports/exportService";
import { ExportAdequacySummariesModel } from "@app/exports/exportModels";
import { GenerateSection } from "../components/GenerateSection";
import { ColumnConfigurationSection } from "../components/ColumnConfigurationSection";
import { ServiceAreaSection } from "../components/ServiceAreaSection";
import { SpecialtySection } from "../components/SpecialtySection";
import { handleToastSuccess, handleToastError, reportTypeToColumnsEndpointMap } from "../helpers";
import { ReportCustomizationReducerActions } from "../interfaces/reportCustomizationReducerActionsEnum";
import { FeatureFlagContext, Features } from "@app/feature-flags";
import { NetworkAssessmentAnalysisType } from "@app/projects/enumerators";
import { ReportType } from "../interfaces/reportTypeEnum";

export const SpecialtySummaryByCountyReport: FC = () => {
  const rcContext = useContext(ReportCustomizationContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const exportService = useRef(new ExportService()).current;

  const getAdequacySpecialtySummariesExportModel = () => {
    let membershipType;
    if (rcContext.state.selectedMemberships.length === 2)
      membershipType = MembershipType.Both;
    else if (rcContext.state.selectedMemberships[0]?.key === rcContext.routeState.primaryMembership.id)
      membershipType = MembershipType.Primary;
    else
      membershipType = MembershipType.Secondary;

    const specialtySummaryModel = {
      changedGapsOnly: !!rcContext.state.changedGapsType,
      comparedProjectRunConfigurationId: parseInt(rcContext.state.comparedTimeslice),
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      includeActiveRegionSets: rcContext.state.includeActive,
      includeApprovedRegionSets: rcContext.state.includeApproved,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePendingRegionSets: rcContext.state.includePending,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      membershipType,
      networkAssessmentAnalysisTypes: rcContext.state.networkAssessmentAnalysisTypes,
      projectRunConfigurationId: parseInt(rcContext.state.timeslice),
      projectURL: `${window.customUIAbsolutePath}/projects/${rcContext.routeParams.projectId}`,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: rcContext.state.selectedCounties.map((sa) => parseInt(sa)),
      reportCustomizationId: rcContext.state.selectedPreset?.id,
      runAsynchronous: true,
      sharedUsers: rcContext.state.sharedUsers,
      showExpandableSpecialties: rcContext.state.includeExpandableSpecialties,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key)
    };

    return specialtySummaryModel;
  }

  const getAdequacySummariesExportModel = (): ExportAdequacySummariesModel => {
    let includedNetworkAssessmentAnalysisType;
    if (rcContext.state.includeActive || rcContext.state.includePending || rcContext.state.includeApproved) {
      includedNetworkAssessmentAnalysisType = NetworkAssessmentAnalysisType.ServiceArea;
    } else {
      includedNetworkAssessmentAnalysisType = NetworkAssessmentAnalysisType.Opportunity;
    }

    return {
      changedGapsOnly: !!rcContext.state.changedGapsType,
      comparedProjectRunConfigurationId: parseInt(rcContext.state.comparedTimeslice),
      customProjectId: parseInt(rcContext.routeParams.projectId),
      eMailOnSuccess: rcContext.state.shouldEmailLink,
      exportName: rcContext.state.filename,
      formatType: rcContext.state.fileFormat,
      includeActiveRegionSets: rcContext.state.includeActive,
      includeApprovedRegionSets: rcContext.state.includeApproved,
      includeExpandableSpecialties: rcContext.state.includeExpandableSpecialties,
      includeFacilitySpecialties: rcContext.state.includeFacilitySpecialties,
      includePendingRegionSets: rcContext.state.includePending,
      includePhysicianSpecialties: rcContext.state.includePhysicianSpecialties,
      networkAssessmentAnalysisType: includedNetworkAssessmentAnalysisType,
      projectRunConfigurationId: parseInt(rcContext.state.timeslice),
      projectURL: `${window.customUIAbsolutePath}/projects/${rcContext.routeParams.projectId}`,
      regionSetGapFilter: rcContext.state.serviceAreaGapsType,
      regionSetIds: rcContext.state.selectedCounties.map((sa) => parseInt(sa)),
      reportCustomizationId: rcContext.state.selectedPreset?.id,
      runAsynchronous: true,
      sharedUsers: rcContext.state.sharedUsers,
      showCMSCredit: true,
      specialtyGapFilter: rcContext.state.specialtyGapsType,
      specialtyGroupCodes: rcContext.state.selectedSpecialties.map((sp) => sp.key),
    };
  }

  // event handlers
  const handleGenerateClick = () => {
    rcContext.dispatch({ payload: { appendNextClosestProviders: true }, type: ReportCustomizationReducerActions.ChangeIsGenerating });

    const promise = enabledFeatureFlags.has(Features.SPECIALTY_SUMMARY_REPORT_ALIGNMENT)
      ? exportService.postExportAdequacySpecialtySummaries(getAdequacySpecialtySummariesExportModel())
      : exportService.postExportAdequacySummaries(getAdequacySummariesExportModel());

    promise
      .then(() => handleToastSuccess(rcContext.state.filename),
        () => handleToastError(rcContext.state.filename)
      ).then(() => {
        rcContext.dispatch({ payload: { appendNextClosestProviders: false }, type: ReportCustomizationReducerActions.ChangeIsGenerating })
      });
  }

  return (
    <>
      <div className="container ml-0">
        <ServiceAreaSection />

        <SpecialtySection />

        <ColumnConfigurationSection columnsEndpoint={reportTypeToColumnsEndpointMap.get(ReportType.SpecialtySummaryByCounty)} exportModel={getAdequacySpecialtySummariesExportModel()} />
      </div>
      <GenerateSection
        settingsAreValid={rcContext.validateSettings()}
        onGenerate={handleGenerateClick}
      />
    </>
  );
}